import { Component, ElementRef, OnInit, OnDestroy, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { MessageModule } from 'primeng/message';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { RippleModule } from 'primeng/ripple';
import { TemporalLinkService } from '@app/@shared/temporal-link.service';
import { EmailVerificationService } from '@app/@shared/email-verification.service';
import { LinkExpirationModule } from '@app/@shared/link-expiration/link-expiration.module';
import { interval, Subscription, timer } from 'rxjs';
import { TemporaryLink } from '@api-swagger-client/index';

/** Llave donde el login deja el correo pendiente de verificar (fallback al refrescar). */
export const PENDING_VERIFICATION_EMAIL_KEY = 'pendingVerificationEmail';

/** Segundos de espera antes de poder reenviar el código. */
const RESEND_COOLDOWN_SECONDS = 60;
/** Segundos que se muestra el éxito antes de redirigir al login. */
const REDIRECT_SECONDS = 4;
const CODE_LENGTH = 6;

/**
 * Fase 4 — pantalla de verificación de correo con DOS modos de entrada, un solo
 * componente (no hay ruta nueva; esta ya existía y vive fuera del `authGuard`,
 * correcto porque el usuario aún no tiene sesión):
 *
 * - **Modo link:** llega `?token=` del correo. Se valida y se **consume solo al
 *   cargar** (antes exigía un clic manual, parte del hallazgo V4).
 * - **Modo código:** no llega token; lo redirige el login. Muestra 6 casillas y un
 *   botón de reenviar. Es el caso que necesita la app móvil, porque el link del
 *   correo abre el navegador, no la app.
 *
 * Ambos modos terminan en el mismo estado de éxito, con loader de redirección
 * automática al login (antes dejaba botones para que el usuario decidiera).
 *
 * El diseño reusa el patrón de `auth/password-update` (contenedor + tarjeta + header
 * con logo + `form-field` + `custom-primary-button` + footer de ayuda) y el
 * componente compartido `app-link-expiration` para la vigencia del enlace, en vez
 * del cronómetro propio que tenía hardcodeadas 3 h.
 */
@Component({
    selector: 'app-verify-email',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ButtonModule,
        RippleModule,
        InputTextModule,
        ProgressSpinnerModule,
        MessageModule,
        ToastModule,
        LinkExpirationModule,
    ],
    templateUrl: './verify-email.component.html',
    styleUrls: ['./verify-email.component.scss'],
    providers: [MessageService],
})
export class VerifyEmailComponent implements OnInit, OnDestroy {
    @ViewChildren('codeBox') codeBoxes!: QueryList<ElementRef<HTMLInputElement>>;

    /** 'link' = token en la URL · 'code' = casillas de 6 dígitos. */
    mode: 'link' | 'code' = 'link';

    token: string | null = null;
    referenceId: string | null = null;
    loading: boolean = true;
    error: string | null = null;
    success: boolean = false;
    verifying: boolean = false;

    // Modo código
    email: string = '';
    digits: string[] = Array(CODE_LENGTH).fill('');
    resending: boolean = false;
    resendCooldown: number = 0;
    readonly codeLength = CODE_LENGTH;
    readonly boxes = Array.from({ length: CODE_LENGTH }, (_, i) => i);

    // Éxito / redirección
    redirectSeconds: number = REDIRECT_SECONDS;

    // Vigencia del enlace (modo link) — la maneja `app-link-expiration`
    expirationDate: Date | null = null;
    linkExpired: boolean = false;
    linkDetails: any = null;

    private cooldownSubscription: Subscription | null = null;
    private redirectSubscription: Subscription | null = null;

    constructor(
        private activatedRoute: ActivatedRoute,
        private temporalLinkService: TemporalLinkService,
        private emailVerificationService: EmailVerificationService,
        private router: Router,
        private messageService: MessageService
    ) {}

    ngOnInit(): void {
        this.activatedRoute.queryParams.subscribe((params) => {
            this.token = params['token'];
            this.referenceId = params['referenceId'];
            if (this.token) {
                this.mode = 'link';
                this.validateLink();
            } else {
                // Modo código: el login redirige aquí sin token. El correo llega por
                // el state del router y se respalda en sessionStorage para sobrevivir
                // a un refresh de la página; si no hay ninguno, se pide al usuario.
                this.mode = 'code';
                this.email = this.resolvePendingEmail(params['email']);
                this.loading = false;
                timer(150).subscribe(() => this.focusBox(0));
            }
        });
    }

    ngOnDestroy(): void {
        this.cooldownSubscription?.unsubscribe();
        this.redirectSubscription?.unsubscribe();
    }

    private resolvePendingEmail(emailFromQuery?: string): string {
        const fromState = (history.state?.email as string) || '';
        const stored = sessionStorage.getItem(PENDING_VERIFICATION_EMAIL_KEY) || '';
        const email = fromState || emailFromQuery || stored;
        if (email) {
            sessionStorage.setItem(PENDING_VERIFICATION_EMAIL_KEY, email);
        }
        return email;
    }

    // ─────────────────────────── Modo link ───────────────────────────

    validateLink(): void {
        if (!this.token) {
            this.error = 'Token no proporcionado';
            this.loading = false;
            return;
        }

        this.temporalLinkService.validateTemporalLink(this.token).subscribe({
            next: (response) => {
                if (response && response.valid) {
                    this.linkDetails = response.link;

                    if (this.linkDetails?.expirationDate) {
                        this.expirationDate = new Date(this.linkDetails.expirationDate);
                    }

                    if (this.linkDetails?.used) {
                        // Ya verificado antes: no hay nada que consumir.
                        this.loading = false;
                        this.onVerified('Este correo ya había sido verificado.');
                        return;
                    }

                    // V4: se consume solo al cargar, sin clic manual.
                    this.verifyEmail();
                } else {
                    this.error = response?.message || 'El enlace de verificación no es válido';
                    this.loading = false;
                }
            },
            error: (err) => {
                this.error = this.describeError(err, 'Error al verificar el correo');
                this.loading = false;
                this.notify('error', 'Error de verificación', this.error);
            },
        });
    }

    verifyEmail(): void {
        if (!this.token) return;

        this.verifying = true;
        this.temporalLinkService.verifyEmail(this.token).subscribe({
            next: (response: TemporaryLink) => {
                this.verifying = false;
                this.loading = false;
                if (response && response.used === true) {
                    this.onVerified();
                } else {
                    this.error = 'No se pudo verificar el correo electrónico';
                    this.notify('error', 'Error de verificación', this.error);
                }
            },
            error: (err) => {
                this.verifying = false;
                this.loading = false;
                this.error = this.describeError(err, 'Error al verificar el correo');
                this.notify('error', 'Error de verificación', this.error);
            },
        });
    }

    onExpirationStatusChanged(expired: boolean): void {
        this.linkExpired = expired;
    }

    /** Del modo link fallido se puede saltar al modo código sin salir de la pantalla. */
    switchToCodeMode(): void {
        this.mode = 'code';
        this.error = null;
        this.token = null;
        this.email = this.resolvePendingEmail();
        timer(150).subscribe(() => this.focusBox(0));
    }

    // ─────────────────────────── Modo código ───────────────────────────

    get code(): string {
        return this.digits.join('');
    }

    get codeIsComplete(): boolean {
        return this.code.length === CODE_LENGTH && !this.digits.some((d) => !d);
    }

    get canSubmitCode(): boolean {
        return this.codeIsComplete && !!this.email?.trim() && !this.verifying;
    }

    /** Una casilla por dígito: escribe, avanza el foco y autoenvía al completar. */
    onDigitInput(index: number, event: Event): void {
        const input = event.target as HTMLInputElement;
        const digit = (input.value || '').replace(/\D/g, '').slice(-1);
        this.digits[index] = digit;
        input.value = digit;

        if (digit && index < CODE_LENGTH - 1) {
            this.focusBox(index + 1);
        }
        if (this.codeIsComplete) {
            this.submitCode();
        }
    }

    /** Backspace en una casilla vacía retrocede; flechas navegan. */
    onDigitKeyDown(index: number, event: KeyboardEvent): void {
        if (event.key === 'Backspace' && !this.digits[index] && index > 0) {
            event.preventDefault();
            this.digits[index - 1] = '';
            this.setBoxValue(index - 1, '');
            this.focusBox(index - 1);
        } else if (event.key === 'ArrowLeft' && index > 0) {
            this.focusBox(index - 1);
        } else if (event.key === 'ArrowRight' && index < CODE_LENGTH - 1) {
            this.focusBox(index + 1);
        } else if (event.key === 'Enter') {
            this.submitCode();
        }
    }

    /** Pegar el código completo desde el correo llena todas las casillas. */
    onDigitPaste(event: ClipboardEvent): void {
        const pasted = (event.clipboardData?.getData('text') || '').replace(/\D/g, '');
        if (!pasted) return;
        event.preventDefault();

        for (let i = 0; i < CODE_LENGTH; i++) {
            const digit = pasted[i] ?? '';
            this.digits[i] = digit;
            this.setBoxValue(i, digit);
        }
        const lastFilled = Math.min(pasted.length, CODE_LENGTH) - 1;
        this.focusBox(Math.max(lastFilled, 0));
        if (this.codeIsComplete) {
            this.submitCode();
        }
    }

    private focusBox(index: number): void {
        const box = this.codeBoxes?.get(index);
        box?.nativeElement.focus();
        box?.nativeElement.select();
    }

    private setBoxValue(index: number, value: string): void {
        const box = this.codeBoxes?.get(index);
        if (box) {
            box.nativeElement.value = value;
        }
    }

    private clearCode(): void {
        this.digits = Array(CODE_LENGTH).fill('');
        this.boxes.forEach((i) => this.setBoxValue(i, ''));
        this.focusBox(0);
    }

    submitCode(): void {
        if (!this.canSubmitCode) return;

        this.verifying = true;
        this.error = null;
        this.emailVerificationService.verifyCode(this.email.trim(), this.code).subscribe({
            next: () => {
                this.verifying = false;
                this.onVerified();
            },
            error: (err) => {
                this.verifying = false;
                // El backend distingue: código incorrecto (400), vencido/inexistente
                // (410), formato inválido (422) e intentos agotados (429).
                this.error = this.describeError(err, 'No se pudo verificar el código');
                this.clearCode();
                this.notify('error', 'Verificación fallida', this.error);
            },
        });
    }

    resendCode(): void {
        if (!this.email?.trim() || this.resending || this.resendCooldown > 0) return;

        this.resending = true;
        this.emailVerificationService.resend(this.email.trim()).subscribe({
            next: () => {
                this.resending = false;
                this.error = null;
                this.clearCode();
                this.startResendCooldown();
                this.notify(
                    'success',
                    'Código enviado',
                    'Si el correo existe y falta verificarlo, te enviamos un código nuevo.'
                );
            },
            error: (err) => {
                this.resending = false;
                const detail = this.describeError(err, 'No se pudo reenviar el código');
                this.notify('error', 'Error al reenviar', detail);
            },
        });
    }

    private startResendCooldown(): void {
        this.resendCooldown = RESEND_COOLDOWN_SECONDS;
        this.cooldownSubscription?.unsubscribe();
        this.cooldownSubscription = interval(1000).subscribe(() => {
            this.resendCooldown -= 1;
            if (this.resendCooldown <= 0) {
                this.cooldownSubscription?.unsubscribe();
                this.resendCooldown = 0;
            }
        });
    }

    // ─────────────────────────── Común ───────────────────────────

    private onVerified(message?: string): void {
        this.success = true;
        this.error = null;
        this.loading = false;
        if (this.linkDetails) {
            this.linkDetails.used = true;
        }
        sessionStorage.removeItem(PENDING_VERIFICATION_EMAIL_KEY);
        this.notify('success', 'Correo verificado', message ?? 'Tu correo electrónico quedó verificado.');
        this.startRedirectCountdown();
    }

    /** Cuenta atrás visible + redirección automática al login. */
    private startRedirectCountdown(): void {
        this.redirectSeconds = REDIRECT_SECONDS;
        this.redirectSubscription?.unsubscribe();
        this.redirectSubscription = interval(1000).subscribe(() => {
            this.redirectSeconds -= 1;
            if (this.redirectSeconds <= 0) {
                this.redirectSubscription?.unsubscribe();
                this.goToLogin();
            }
        });
    }

    private notify(severity: 'success' | 'info' | 'warn' | 'error', summary: string, detail: string): void {
        this.messageService.add({ key: 'tst', severity, summary, detail, life: 4000 });
    }

    /** Mensaje del backend cuando exista; si no, uno genérico. */
    private describeError(err: any, fallback: string): string {
        return err?.error?.message || err?.error?.error?.message || err?.message || fallback;
    }

    goToLogin(): void {
        this.router.navigate(['/auth/login']);
    }

    goToHome(): void {
        this.router.navigate(['/home']);
    }
}
